import { ReactNode } from 'react'
import { FullUser, Gender } from '@hooks/useUser'
import { SvgIconName } from '@components/SvgIcon/types'

export enum CommentAuthorType {
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR',
  JOURNALIST = 'JOURNALIST',
  USER = 'USER',
}

export enum CommentAnswerStatus {
  Active = 'ACTIVE',
  Pending = 'PENDING',
}

interface CommentOrAnswer {
  id: number
  author_type: CommentAuthorType
  discussion_id: number
  created: string
  body: string
  top_rated: boolean
  status: CommentAnswerStatus
  user: FullUser
  // reactions
  like_count: number
  dislike_count: number
  reaction1_count: number
  reaction2_count: number
  reaction3_count: number
}

export interface Answer extends CommentOrAnswer {
  father_id: number
}

export interface Comment extends CommentOrAnswer {
  number_of_answers: number
  answers: Answer[]
}

export interface ArticleComment extends Comment {
  meta?: CommentMeta
  postedAnswer?: Answer
  hasNetworkError?: boolean
}

export interface ArticleMeta {
  currentPage: number
  totalPages: number
  totalElements: number
  numberOfComments: number
  loading: boolean
}

interface CommentMeta {
  answers: {
    currentPage: number
    totalPages: number
    initial: boolean
  }
}

export type Reaction =
  | 'like'
  | 'dislike'
  | 'reaction1'
  | 'reaction2'
  | 'reaction3'

export type LocalReaction = 'heart' | 'bolt' | 'happy' | 'sad' | 'critical'

export interface Reactions {
  [commentingItemId: string]: Reaction | null
}

// the suffix "value" is inspired by form frameworks
export type OwnReactionValues = { [reaction in LocalReaction]: boolean }
export type CommentType = 'comment' | 'answer'
export type CommentActionType = 'report' | 'reply' | 'delete'

export interface RequestConfig {
  type: string
  path: string
  pathReplacements?: { find: string; replace: any }[]
  payload?: object
}

export interface CommentingStateProps {
  postedComment?: Comment
  commentIds: number[]
  topCommentIds: number[]
  initialReactions?: Reactions
  reactions?: Reactions
  meta: ArticleMeta
  articleId: string
  section: string
  postingEnabled: boolean
  apiDownError: boolean
  getTitle: ({
    name,
    statusId,
    authorType,
  }: {
    name: string
    statusId?: number
    authorType?: CommentAuthorType
  }) => string
  charactersLeftText: (numberOfCharacters: number) => string
  characterLimit: number
  confirmationMessageTimeout: number
  nativeQueryParams: string
}

export interface CommentingInternalStateProps {
  postingComment: boolean
  setPostingComment: (postingComment: boolean) => void
  requireFullLogin: (action: () => void) => () => void
}

export interface CommentingConfirmationMessageProps {
  onTimeout: () => void
  children: ReactNode
}

export interface CommentingTitleProps {
  children: string
  onCommentsDisplayed: () => void
}

export interface CommentingItemProps {
  type: string
  authorType: CommentAuthorType
  gender: Gender
  deleting: boolean
  isOwnItem: boolean
  isTopRated?: boolean
  authorName: string
  datePublished: string
  textContent: string
  allowReports?: boolean
  allowReplies?: boolean
  onDelete: () => void
  onReport: () => void
  onReply: () => void
  commentOrAnswer: Comment | Answer
}

export type PostedCommentingItemProps = Pick<
  CommentingItemProps,
  | 'type'
  | 'authorType'
  | 'gender'
  | 'deleting'
  | 'authorName'
  | 'datePublished'
  | 'textContent'
  | 'onDelete'
>

export interface CommentProps {
  postingEnabled: boolean
  isTopComment?: boolean
  comment: Comment
}

export interface PostedCommentProps {
  comment: Comment
}

export interface PostedAnswerProps {
  answer: Answer
}

export interface AnswerProps {
  answer: Answer
  postingEnabled: boolean
  onReply: (initialText: string) => void
}

export interface CommentingActionPopupProps {
  onConfirm: (textValue?: string) => void
  onCancel: () => void
  type: CommentType
  actionType: CommentActionType
  initialText?: string
}

export interface CommentingActionPopupFormProps {
  actionType: CommentActionType
  onConfirm: (textValue?: string) => void
  onCancel: () => void
  textInput: string
}

export interface CommentingItemWrapperProps {
  type: CommentType
  itemId: number
  children: ReactNode
  isOwnItem: boolean
  isTopComment?: boolean
}

export interface CommentingPromptWrapperProps {
  type: CommentType
  children: ReactNode
}

export interface CommentingPromptProps {
  type: CommentType
  children?: undefined
  onClick: () => void
}

export interface CommentingItemListProps {
  type: CommentType
  children: ReactNode
  rerenderKey: string
  moreToShow: boolean
  onShowMore?: () => void
}

export interface ActionPopupTitleProps {
  actionType?: CommentActionType
  children: ReactNode
}

export interface ActionPopupContentProps {
  actionType: CommentActionType
  children: ReactNode
}

export interface ActionPopupTextInputProps {
  invalid: boolean
  textValue: string
  onChange: (value: string) => void
}

export interface ActionPopupTextLimitProps {
  invalid: boolean
  children: ReactNode
}

export interface ActionPopupActionsConfirmProps {
  children: string
  onClick: () => void
}

export interface ActionPopupActionsCancelProps {
  children: string
  onClick: () => void
}

export interface ItemContentProps {
  isOwnItem: boolean
  deletingItem: boolean
  isSuperUser: boolean
  children: ReactNode
}

type ReactionIcon = 'heart' | 'bolt' | 'happy' | 'sad' | 'critical'

export interface ReactionIconMapping {
  heart: SvgIconName
  bolt: SvgIconName
  happy: SvgIconName
  sad: SvgIconName
  critical: SvgIconName
}

export interface ItemActionReactProps {
  name: ReactionIcon
  hasOwnReaction: boolean
  children: number
  onAction: () => void
}

export interface ItemSuperUserIndicatorProps {
  authorType: CommentAuthorType
}

export interface CommentingAdProps extends Record<string, unknown> {
  placement: string
  slotCode: string
}
export interface CommentingProps {
  id: string
  commentsEnabled: boolean
  postingEnabled: boolean
}

export interface APICommentingData {
  content: Comment[]
  number: number
  last: boolean
  number_of_comments: number
}

export interface QueryCommentingData {
  pages: APICommentingData[]
}

export interface APICommentingAnswersData {
  content: Answer[]
}
